// frontend/src/api/database.js
// Created: April 8, 2025
// This file contains functions for interacting with the database API

import databaseClient from './databaseClient';

// Cache for storing API responses
const cache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1000; // 1 second between requests

/**
 * Get leads from the database
 * @param {string|object} source - Source filter ('all' or object with filters)
 * @returns {Promise<Array>} Array of leads
 */
export const getDatabaseLeads = async (source = 'all') => {
  try {
    // Create cache key based on source
    const cacheKey = typeof source === 'string' ? source : JSON.stringify(source);
    
    // Check cache first
    const cachedData = cache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
      console.log('Returning cached database leads for source:', source);
      return cachedData.data;
    }
    
    // Debounce requests
    const now = Date.now();
    if (now - lastRequestTime < MIN_REQUEST_INTERVAL) {
      console.log('Debouncing database leads request');
      await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL));
    }
    
    // Prepare query parameters
    const params = typeof source === 'string' 
      ? { source: source === 'all' ? undefined : source }
      : source;
    
    console.log('Fetching database leads with params:', params);
    
    // Make API request - CHANGED THIS LINE TO USE THE CORRECT PATH
    const response = await databaseClient.get('/admin/leads', { params });
    lastRequestTime = Date.now();
    
    // Add debugging logs
    console.log('Raw response:', response);
    console.log('Response data type:', typeof response.data);
    console.log('Response data:', response.data);
    
    // The data might be nested in response.data.data
    const leadsData = Array.isArray(response.data) ? response.data : 
                     (response.data.data && Array.isArray(response.data.data) ? response.data.data : []);
    
    console.log('Extracted leads data:', leadsData);
    
    // Update cache
    cache.set(cacheKey, {
      data: {
        success: true,
        data: leadsData
      },
      timestamp: lastRequestTime
    });
    
    console.log('Fetched database leads:', leadsData.length);
    return {
      success: true,
      data: leadsData
    };
  } catch (error) {
    console.error('Error fetching database leads:', error);
    throw error;
  }
};

/**
 * Get a specific lead from the database
 * @param {number} id - The ID of the lead
 * @returns {Promise} API response with lead details
 */
export const getDatabaseLead = async (id) => {
  try {
    const response = await databaseClient.get(`/leads/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching database lead ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new lead in the database
 * @param {Object} leadData - The data for the new lead
 * @returns {Promise} API response
 */
export const createDatabaseLead = async (leadData) => {
  try {
    const response = await databaseClient.post('/leads', leadData);
    return response.data;
  } catch (error) {
    console.error('Error creating database lead:', error);
    throw error;
  }
};

/**
 * Update a lead in the database
 * @param {number} id - The ID of the lead
 * @param {Object} leadData - The data to update
 * @returns {Promise} API response
 */
export const updateDatabaseLead = async (id, leadData) => {
  try {
    const response = await databaseClient.put(`/leads/${id}`, leadData);
    return response.data;
  } catch (error) {
    console.error(`Error updating database lead ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a lead from the database
 * @param {number} id - The ID of the lead
 * @returns {Promise} API response
 */
export const deleteDatabaseLead = async (id) => {
  try {
    const response = await databaseClient.delete(`/leads/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error deleting database lead ${id}:`, error);
    throw error;
  }
};

/**
 * Get leads statistics
 * @returns {Promise} API response
 */
export const getDatabaseLeadsStats = async () => {
  try {
    const response = await databaseClient.get('/leads/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching database leads statistics:', error);
    throw error;
  }
};

/**
 * Get agents from the Railway database
 * @returns {Promise<Array>} Array of agents
 */
export const getDatabaseAgents = async () => {
  try {
    // Create cache key
    const cacheKey = 'agents';
    
    // Check cache first
    const cachedData = cache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
      console.log('Returning cached database agents');
      return {
        success: true,
        data: cachedData.data
      };
    }
    
    // Debounce requests
    const now = Date.now();
    if (now - lastRequestTime < MIN_REQUEST_INTERVAL) {
      console.log('Debouncing database agents request');
      await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL));
    }
    
    console.log('Fetching agents from Railway database');
    
    // Make API request - Updated to use the correct endpoint path
    const response = await databaseClient.get('/admin/agents');
    lastRequestTime = Date.now();
    
    // Update cache
    cache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching agents from database:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch agents',
      status: error.response?.status
    };
  }
};

/**
 * Get agents by state from the Railway database
 * @param {string} state - The state code to filter by
 * @returns {Promise<Array>} Array of agents in the specified state
 */
export const getDatabaseAgentsByState = async (state) => {
  try {
    // Create cache key
    const cacheKey = `agents-by-state-${state}`;
    
    // Check cache first
    const cachedData = cache.get(cacheKey);
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_DURATION) {
      console.log('Returning cached database agents by state:', state);
      return {
        success: true,
        data: cachedData.data
      };
    }
    
    // Debounce requests
    const now = Date.now();
    if (now - lastRequestTime < MIN_REQUEST_INTERVAL) {
      console.log('Debouncing database agents by state request');
      await new Promise(resolve => setTimeout(resolve, MIN_REQUEST_INTERVAL));
    }
    
    console.log('Fetching agents by state from Railway database:', state);
    
    // Make API request
    const response = await databaseClient.get(`/agents/by-state/${state}`);
    lastRequestTime = Date.now();
    
    // Update cache
    cache.set(cacheKey, {
      data: response.data,
      timestamp: Date.now()
    });
    
    return {
      success: true,
      data: response.data
    };
  } catch (error) {
    console.error('Error fetching agents by state from database:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch agents by state',
      status: error.response?.status
    };
  }
}; 